import { useState, useEffect } from 'react';
import { Link, useLocation } from 'react-router-dom';
import { categories } from '@/mocks/categories';

export default function Navbar() {
  const [isScrolled, setIsScrolled] = useState(false);
  const [mobileMenuOpen, setMobileMenuOpen] = useState(false);
  const [searchOpen, setSearchOpen] = useState(false);
  const [searchQuery, setSearchQuery] = useState('');
  const location = useLocation();

  useEffect(() => {
    const handleScroll = () => {
      setIsScrolled(window.scrollY > 80);
    };
    window.addEventListener('scroll', handleScroll);
    return () => window.removeEventListener('scroll', handleScroll);
  }, []);

  useEffect(() => {
    setMobileMenuOpen(false);
    setSearchOpen(false);
  }, [location]);

  const handleSearch = (e: React.FormEvent) => {
    e.preventDefault();
    if (searchQuery.trim()) {
      window.REACT_APP_NAVIGATE(`/category/dianying?q=${encodeURIComponent(searchQuery.trim())}`);
      setSearchOpen(false);
      setSearchQuery('');
    }
  };

  return (
    <nav
      className={`fixed top-0 left-0 right-0 z-50 transition-all duration-300 ${
        isScrolled
          ? 'bg-background-50/95 backdrop-blur-md shadow-sm'
          : 'bg-transparent'
      }`}
    >
      <div className="w-full px-4 md:px-6">
        <div className="flex items-center justify-between h-16">
          <Link
            to="/"
            className="flex items-center gap-2 group"
          >
            <div className="w-9 h-9 rounded-lg bg-primary-500 flex items-center justify-center">
              <i className="ri-play-circle-fill text-background-50 text-xl" style={{ lineHeight: 1 }}></i>
            </div>
            <span className={`text-lg font-bold font-heading whitespace-nowrap transition-colors ${
              isScrolled ? 'text-foreground-900' : 'text-background-50'
            }`}>
              91视频
            </span>
          </Link>

          <div className="hidden md:flex items-center gap-1">
            <Link
              to="/"
              className={`px-4 py-2 text-sm font-medium rounded-lg transition-colors whitespace-nowrap ${
                location.pathname === '/'
                  ? isScrolled ? 'bg-primary-100 text-primary-700' : 'bg-white/20 text-background-50'
                  : isScrolled ? 'text-foreground-700 hover:bg-background-100' : 'text-background-50/80 hover:text-background-50 hover:bg-white/10'
              }`}
            >
              首页
            </Link>
            {categories.slice(0, 6).map((cat) => (
              <Link
                key={cat.slug}
                to={`/category/${cat.slug}`}
                className={`px-4 py-2 text-sm font-medium rounded-lg transition-colors whitespace-nowrap ${
                  location.pathname.startsWith(`/category/${cat.slug}`)
                    ? isScrolled ? 'bg-primary-100 text-primary-700' : 'bg-white/20 text-background-50'
                    : isScrolled ? 'text-foreground-700 hover:bg-background-100' : 'text-background-50/80 hover:text-background-50 hover:bg-white/10'
                }`}
              >
                {cat.name}
              </Link>
            ))}
            <Link
              to="/about"
              className={`px-4 py-2 text-sm font-medium rounded-lg transition-colors whitespace-nowrap ${
                location.pathname === '/about'
                  ? isScrolled ? 'bg-primary-100 text-primary-700' : 'bg-white/20 text-background-50'
                  : isScrolled ? 'text-foreground-700 hover:bg-background-100' : 'text-background-50/80 hover:text-background-50 hover:bg-white/10'
              }`}
            >
              关于我们
            </Link>
          </div>

          <div className="flex items-center gap-2">
            {searchOpen && (
              <form onSubmit={handleSearch} className="hidden md:flex items-center">
                <div className="relative">
                  <input
                    type="text"
                    value={searchQuery}
                    onChange={(e) => setSearchQuery(e.target.value)}
                    placeholder="搜索视频..."
                    className="w-48 pl-9 pr-3 py-2 text-sm rounded-lg bg-background-100 border border-background-200 text-foreground-900 placeholder-foreground-400 focus:outline-none focus:ring-2 focus:ring-primary-400 focus:border-transparent"
                    autoFocus
                  />
                  <i className="ri-search-line absolute left-3 top-1/2 -translate-y-1/2 text-foreground-400 text-sm"></i>
                </div>
              </form>
            )}
            <button
              onClick={() => setSearchOpen(!searchOpen)}
              className={`hidden md:flex w-9 h-9 items-center justify-center rounded-lg transition-colors cursor-pointer ${
                isScrolled ? 'text-foreground-600 hover:bg-background-100' : 'text-background-50 hover:bg-white/10'
              }`}
              aria-label="搜索"
            >
              <i className={`text-lg ${searchOpen ? 'ri-close-line' : 'ri-search-line'}`}></i>
            </button>
            <button
              onClick={() => setMobileMenuOpen(!mobileMenuOpen)}
              className="md:hidden w-9 h-9 flex items-center justify-center rounded-lg transition-colors cursor-pointer text-background-50 hover:bg-white/10"
              aria-label="菜单"
            >
              <i className={`text-lg ${mobileMenuOpen ? 'ri-close-line' : 'ri-menu-line'}`}></i>
            </button>
          </div>
        </div>
      </div>

      {mobileMenuOpen && (
        <div className="md:hidden bg-background-50 border-t border-background-200 shadow-lg">
          <div className="px-4 py-3">
            <form onSubmit={handleSearch} className="mb-3">
              <div className="relative">
                <input
                  type="text"
                  value={searchQuery}
                  onChange={(e) => setSearchQuery(e.target.value)}
                  placeholder="搜索视频..."
                  className="w-full pl-9 pr-3 py-2.5 text-sm rounded-lg bg-background-100 border border-background-200 text-foreground-900 placeholder-foreground-400 focus:outline-none focus:ring-2 focus:ring-primary-400"
                />
                <i className="ri-search-line absolute left-3 top-1/2 -translate-y-1/2 text-foreground-400 text-sm"></i>
              </div>
            </form>
            <div className="space-y-1">
              <Link to="/" className="block px-3 py-2.5 text-sm font-medium rounded-lg text-foreground-700 hover:bg-background-100">首页</Link>
              {categories.map((cat) => (
                <Link key={cat.slug} to={`/category/${cat.slug}`} className="block px-3 py-2.5 text-sm font-medium rounded-lg text-foreground-700 hover:bg-background-100">{cat.name}</Link>
              ))}
              <Link to="/about" className="block px-3 py-2.5 text-sm font-medium rounded-lg text-foreground-700 hover:bg-background-100">关于我们</Link>
            </div>
          </div>
        </div>
      )}
    </nav>
  );
}